'use client'

import { ScheduleTaskModal } from '@/app/scheduled-tasks/ScheduleTaskModal'
import { AnimatedBackground } from '@/components/AnimatedBackground/AnimatedBackground'
import { AnimatedBackgroundOverrideProvider } from '@/components/AnimatedBackground/AnimatedBackgroundOverride'
import ErrorBoundary from '@/components/ErrorBoundary'
import PricingModal from '@/components/Pricing/PricingModal'
import { ReportIssueModal } from '@/components/ReportIssueModal'
import { Sidebar } from '@/components/Sidebar/Sidebar'
import WorkflowPanelLoading from '@/components/Workflow/Preview/WorkflowPanelLoading'
import { ThemeProvider } from '@/components/theme-provider'
import { usePricingModal } from '@/hooks/usePricingModal'
import { copilotEvents, ScheduleTaskPrefill } from '@/lib/features/copilotv2/copilotEvents'
import { useRouter } from 'next/navigation'
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import Session from 'supertokens-web-js/recipe/session'

type CopilotLayoutClientProps = {
  children: React.ReactNode
  initialCookieString?: string
}

export function CopilotLayoutClient({
  children,
  initialCookieString,
}: CopilotLayoutClientProps): ReactElement {
  const router = useRouter()
  const { setOpen: openPricingModal } = usePricingModal()
  const [hasSession, setHasSession] = useState<boolean | null>(null)
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false)
  const [scheduleModalPrefill, setScheduleModalPrefill] = useState<ScheduleTaskPrefill | null>(null)

  const handleError = useCallback(() => {
    console.error('Error in CopilotLayoutClient')
  }, [])

  // Check session on mount and redirect if no session
  useEffect(() => {
    let isMounted = true

    const checkSession = async () => {
      const sessionExists = await Session.doesSessionExist()
      if (!isMounted) {
        return
      }
      if (!sessionExists) {
        setHasSession(false)
        // If there's already a redirect param, preserve it; otherwise use current pathname + search params
        const searchParams = new URLSearchParams(window.location.search)
        const existingRedirect = searchParams.get('redirect')
        const redirectTarget = existingRedirect || window.location.pathname + window.location.search
        router.push(`/?signup=1&redirect=${encodeURIComponent(redirectTarget)}`)
        return
      }
      setHasSession(true)
    }
    void checkSession()

    return () => {
      isMounted = false
    }
  }, [router])

  // Handle copilot events
  useEffect(() => {
    const cleanup = copilotEvents.addEventListener((event) => {
      if (event.type === 'openBillingModal') {
        openPricingModal({ highlightedPlanId: event.data.highlightedPlanId })
      } else if (event.type === 'openScheduleModal') {
        setScheduleModalPrefill(event.data.prefill)
        setScheduleModalOpen(true)
      }
    })

    return cleanup
  }, [openPricingModal])

  const fallbackMessage = useMemo(() => {
    if (hasSession === false) {
      return 'Redirecting you to sign up...'
    }
    return 'Preparing your workspace...'
  }, [hasSession])

  if (hasSession !== true) {
    return (
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <AnimatedBackgroundOverrideProvider>
          <div className="relative min-h-screen w-full max-w-[100vw] overflow-x-hidden bg-[#F2F3F7] text-black">
            <AnimatedBackground />
            <div className="absolute left-0 top-0 z-10 flex h-full w-full overflow-y-hidden">
              <Sidebar initialCookieString={initialCookieString} />
              <div className="flex h-full flex-1 flex-col overflow-y-hidden">
                <div className="flex h-full w-full items-center justify-center px-6 text-black">
                  <div className="flex w-full max-w-md flex-col items-center gap-4 text-center">
                    <span className="sr-only">Loading Copilot workspace</span>
                    <WorkflowPanelLoading height={36} />
                    <p className="text-sm text-black/80">{fallbackMessage}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedBackgroundOverrideProvider>
      </ThemeProvider>
    )
  }

  return (
    <ErrorBoundary onError={handleError}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <AnimatedBackgroundOverrideProvider>
          <div className="min-h-screen w-full max-w-[100vw] overflow-x-hidden bg-[#F2F3F7] text-black">
            <AnimatedBackground />
            <div className="absolute left-0 top-0 z-10 flex h-full w-full">
              <Sidebar initialCookieString={initialCookieString} />
              <div className="flex h-full flex-1 flex-col overflow-y-hidden">{children}</div>
            </div>
          </div>
          <ReportIssueModal />
          <PricingModal />
          {scheduleModalPrefill && (
            <ScheduleTaskModal
              isOpen={scheduleModalOpen}
              onClose={() => {
                setScheduleModalOpen(false)
                // Keep prefill data so user can click button to reopen
              }}
              toolCalls={scheduleModalPrefill.toolCalls}
              toolName={
                scheduleModalPrefill.toolCalls.length > 1
                  ? `${scheduleModalPrefill.toolCalls.length} tools`
                  : scheduleModalPrefill.toolCalls[0]?.function.name || 'Scheduled Task'
              }
              prefill={scheduleModalPrefill}
            />
          )}
        </AnimatedBackgroundOverrideProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
